import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, CheckCircle2, Users, TrendingUp, Target, 
  Building2, Utensils, BarChart3, Leaf, Handshake, Clock, 
  AlertTriangle, DollarSign, Recycle, UserCheck, ChefHat,
  Workflow, FileCheck, Settings, HeartHandshake, Linkedin,
  Mail, Phone, MapPin, ChevronDown, ChevronUp, Quote,
  LayoutGrid, Scale, Gauge, ShieldCheck, Sparkles
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CafetrixLabsDetail = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const painPoints = [
    {
      icon: <Clock size={40} />,
      title: 'Endless Queues, Unhappy Employees',
      description: 'Long wait times during peak hours lead to frustrated employees and reduced productivity.'
    },
    {
      icon: <Utensils size={40} />,
      title: 'Menu Mismatch & Cost Spirals',
      description: 'Menus that don\'t match team preferences result in high plate waste and escalating costs.'
    },
    {
      icon: <FileCheck size={40} />,
      title: 'Provider Selection & RFP Overload',
      description: 'Choosing the right food service provider is complex, time-consuming, and often overwhelming.'
    },
    {
      icon: <Recycle size={40} />,
      title: 'Food Waste & ESG Reporting Pressure',
      description: 'Managing food waste while meeting sustainability goals and ESG reporting requirements.'
    }
  ];

  const fiveLenses = [
    {
      icon: <LayoutGrid size={48} />,
      title: 'Space & Flow',
      description: 'Cafeteria space optimization, seating dynamics, queue flow, and right-fit kitchen design.',
      color: '#00D4FF'
    },
    {
      icon: <ChefHat size={48} />,
      title: 'Food & Fit',
      description: 'Menu and cuisine fitment for workforce, day-part strategy, and production logic.',
      color: '#FF6B6B'
    },
    {
      icon: <Gauge size={48} />,
      title: 'Cost & Efficiency',
      description: 'Equipment efficiency, throughput, labor deployment, and financial viability analysis.',
      color: '#FFD93D'
    },
    {
      icon: <Leaf size={48} />,
      title: 'Sustain & Care',
      description: 'Food and water waste management, operational hygiene, ESG impact, and satisfaction scores.',
      color: '#6BCB77'
    },
    {
      icon: <Handshake size={48} />,
      title: 'Win-Win Commercials',
      description: 'Optimal commercial models and fair pricing structures for all stakeholders.',
      color: '#9B59B6'
    }
  ];

  const services = [
    {
      icon: <ShieldCheck size={40} />,
      title: 'Comprehensive Operational Audit',
      description: '360° assessment of your entire cafeteria ecosystem covering space, menu, operations, and sustainability.'
    },
    {
      icon: <ChefHat size={40} />,
      title: 'Menu & Kitchen Design Optimization',
      description: 'Strategic menu planning and kitchen layout redesign for maximum efficiency and employee satisfaction.'
    },
    {
      icon: <Recycle size={40} />,
      title: 'Food & Water Waste Management',
      description: 'Sustainable solutions to reduce waste, lower costs, and meet ESG reporting requirements.'
    },
    {
      icon: <Users size={40} />,
      title: 'Manpower Optimization',
      description: 'Right-sizing staff deployment, shift planning, and productivity enhancement strategies.'
    },
    {
      icon: <UserCheck size={40} />,
      title: 'Customer Satisfaction Assessment',
      description: 'Data-driven analysis of employee feedback, dining experience, and service quality metrics.'
    },
    {
      icon: <Scale size={40} />,
      title: 'Win-Win Commercial Structuring',
      description: 'Fair pricing validation, contract structuring, and commercial model design for sustainable partnerships.'
    }
  ];

  const processSteps = [
    {
      phase: 'Phase 1',
      title: 'Discovery & Assessment',
      duration: '1-2 weeks',
      description: 'Comprehensive on-site audit, stakeholder interviews, and data collection across all five lenses.'
    },
    {
      phase: 'Phase 2',
      title: 'Analysis & Benchmarking',
      duration: '1-2 weeks',
      description: 'Deep-dive analysis, industry benchmarking, and gap identification with quantified impact.'
    },
    {
      phase: 'Phase 3',
      title: 'Recommendations & Roadmap',
      duration: '1 week',
      description: 'Detailed 90-180 day action blueprint with prioritized recommendations and projected ROI.'
    },
    {
      phase: 'Phase 4',
      title: 'Implementation Support',
      duration: '2-3 months',
      description: 'Hands-on guidance for executing changes, vendor coordination, and progress tracking.'
    },
    {
      phase: 'Phase 5',
      title: 'Ongoing Optimization',
      duration: 'Quarterly/Annual',
      description: 'Regular reviews, performance monitoring, and continuous improvement cycles.'
    }
  ];

  const faqs = [
    {
      question: 'What makes CafetriX Labs different from food safety auditors?',
      answer: 'Traditional food safety auditors focus solely on compliance. CafetriX Labs provides a comprehensive 360° operational audit that examines your entire cafeteria ecosystem—space planning, menu fitment, cost efficiency, sustainability, and commercial models—delivering actionable insights that drive real operational improvements.'
    },
    {
      question: 'How long does a typical engagement take?',
      answer: 'A standard engagement runs 6-12 weeks from initial assessment to final recommendations. Implementation support can extend 2-3 months additionally. We also offer quarterly and annual optimization programs for ongoing improvement.'
    },
    {
      question: 'What ROI can we expect from recommendations?',
      answer: 'Typical engagements deliver 15-25% cost savings through operational efficiencies, 40-70% reduction in queue times, 20-30% improvement in employee satisfaction scores, and significant reduction in food waste—often achieving full ROI within 6-12 months.'
    },
    {
      question: 'Do you work with catering companies and corporate clients?',
      answer: 'Yes! We serve both corporate facilities teams managing in-house cafeterias and institutional catering companies looking to optimize operations, validate menus, redesign kitchens, or structure win-win commercial agreements.'
    },
    {
      question: 'What is the cost of a diagnostic?',
      answer: 'We offer a complimentary 30-minute diagnostic call to understand your challenges and share three immediate wins. Full engagement pricing is customized based on cafeteria size, complexity, and scope of work required.'
    }
  ];

  const traditionalVsCafetrix = {
    traditional: [
      'Compliance-focused checklists',
      'Surface-level assessments',
      'Limited operational insights',
      'No commercial guidance',
      'One-time audit reports',
      'Generic recommendations'
    ],
    cafetrix: [
      'Holistic ecosystem analysis',
      'Deep-dive operational audit',
      'Actionable improvement roadmap',
      'Win-win commercial structuring',
      'Implementation support included',
      'Customized, measurable outcomes'
    ]
  };

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #1a1f71 0%, #2d3494 50%, #1a1f71 100%)',
          padding: '100px 7.6923%',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1567521464027-f127ff144326?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15
          }} />

          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <Link to="/" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#00D4FF',
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '30px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to VDM Solutions
            </Link>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }} className="hero-grid">
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_vdmbusiness/artifacts/v6hrcwjd_ChatGPT%20Image%20Jan%201%2C%202026%2C%2009_07_53%20PM.png" 
                  alt="CafetriX Labs" 
                  style={{ height: '100px', objectFit: 'contain', marginBottom: '20px' }}
                />
                <p style={{ 
                  color: '#00D4FF', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  letterSpacing: '2px',
                  marginBottom: '20px'
                }}>
                  A DIVISION OF VDM BUSINESS INNOVATIONS
                </p>
                <h1 style={{ 
                  fontSize: '44px', 
                  fontWeight: '700', 
                  color: '#ffffff',
                  marginBottom: '20px',
                  lineHeight: '1.2'
                }}>
                  Transform Your Corporate Cafeteria into a <span style={{ color: '#FF6B6B' }}>Strategic Asset</span>
                </h1>
                <p style={{ 
                  fontSize: '20px', 
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '16px',
                  fontWeight: '500'
                }}>
                  360° Operations Audit + Implementation for Corporate Campuses & Catering Companies
                </p>
                <p style={{ 
                  fontSize: '16px', 
                  color: 'rgba(255,255,255,0.7)',
                  marginBottom: '40px',
                  lineHeight: '1.8'
                }}>
                  Built on nearly three decades of hands-on experience managing cafeterias at Cisco, Oracle, Google, and Boeing.
                </p>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <Link to="/cafetrix-labs/schedule" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '18px 36px',
                    background: '#FF6B6B',
                    color: '#ffffff',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#ff5252'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#FF6B6B'}
                  >
                    Schedule Your 30-Minute Diagnostic
                    <ArrowRight size={20} />
                  </Link>
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <img 
                  src="https://images.unsplash.com/photo-1567521464027-f127ff144326?w=800&q=80" 
                  alt="Modern Corporate Cafeteria" 
                  style={{ 
                    width: '100%', 
                    height: '500px', 
                    objectFit: 'cover',
                    border: '4px solid rgba(0, 212, 255, 0.3)'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '20px',
                  right: '20px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '15px'
                }}>
                  <div style={{
                    background: '#1a1f71',
                    padding: '20px 15px',
                    textAlign: 'center',
                    border: '2px solid #00D4FF'
                  }}>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#FF6B6B' }}>28+</div>
                    <div style={{ fontSize: '11px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1px' }}>Years Experience</div>
                  </div>
                  <div style={{
                    background: '#1a1f71',
                    padding: '20px 15px',
                    textAlign: 'center',
                    border: '2px solid #00D4FF'
                  }}>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#FF6B6B' }}>5000+</div>
                    <div style={{ fontSize: '11px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1px' }}>Meals/Day Managed</div>
                  </div>
                  <div style={{
                    background: '#1a1f71',
                    padding: '20px 15px',
                    textAlign: 'center',
                    border: '2px solid #00D4FF'
                  }}>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#FF6B6B' }}>360°</div>
                    <div style={{ fontSize: '11px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1px' }}>Operational Audit</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(255, 107, 107, 0.1)',
                border: '1px solid #FF6B6B',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#FF6B6B',
                fontWeight: '500'
              }}>
                THE CHALLENGE
              </div>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                The Corporate Cafeteria Challenge: <span style={{ color: '#FF6B6B' }}>Real Problems, Daily</span>
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)', maxWidth: '700px', margin: '0 auto' }}>
                You're not alone. These challenges affect corporate campuses worldwide.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '30px'
            }} className="pain-points-grid">
              {painPoints.map((point, index) => (
                <div key={index} style={{
                  padding: '40px 30px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#FF6B6B';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ color: '#FF6B6B', marginBottom: '20px' }}>
                    {point.icon}
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '12px' }}>{point.title}</h3>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '80px',
              alignItems: 'center'
            }} className="intro-grid">
              <div>
                <div style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'rgba(0, 212, 255, 0.1)',
                  border: '1px solid #00D4FF',
                  marginBottom: '20px',
                  fontSize: '14px',
                  color: '#00D4FF',
                  fontWeight: '500'
                }}>
                  MEET CAFETRIX LABS
                </div>
                <h2 className="display-medium" style={{ marginBottom: '30px' }}>
                  The Future of <span style={{ color: '#00D4FF' }}>Corporate Cafeteria Operations</span>
                </h2>
                <p className="body-large" style={{ marginBottom: '24px', lineHeight: '1.8' }}>
                  CafetriX Labs is the new-age cafeteria and catering operations intelligence division from <strong>VDM Business Innovations</strong>.
                </p>
                <p className="body-medium" style={{ marginBottom: '24px', lineHeight: '1.8', color: 'var(--text-muted)' }}>
                  Built on nearly three decades of hands-on experience in corporate food services and facilities. We've operated food programs and cafeterias at <strong style={{ color: '#00D4FF' }}>Cisco, Oracle, Google, and Boeing</strong>.
                </p>
                <p className="body-medium" style={{ marginBottom: '30px', lineHeight: '1.8', color: 'var(--text-muted)' }}>
                  CafetriX Labs exists to transform everyday cafeterias into <strong>high-performance experience hubs</strong> where operations, menu, design, and sustainability work in perfect sync.
                </p>
                <div style={{
                  padding: '24px',
                  background: 'rgba(0, 212, 255, 0.05)',
                  borderLeft: '4px solid #00D4FF'
                }}>
                  <p className="body-medium" style={{ fontStyle: 'italic', color: 'var(--text-primary)' }}>
                    "Creating cafeterias that run smoother, waste less, serve better, and contract smarter."
                  </p>
                </div>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80" 
                  alt="Modern Cafeteria Operations" 
                  style={{ 
                    width: '100%', 
                    height: '500px', 
                    objectFit: 'cover',
                    border: '1px solid var(--border-subtle)'
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* CafetriX Difference Section */}
        <section style={{ 
          background: 'linear-gradient(135deg, #1a1f71 0%, #2d3494 100%)', 
          padding: '100px 7.6923%' 
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{ 
                fontSize: '36px', 
                fontWeight: '700', 
                color: '#ffffff',
                marginBottom: '16px'
              }}>
                Beyond Food Safety Audits: <span style={{ color: '#00D4FF' }}>The CafetriX Difference</span>
              </h2>
              <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.7)', maxWidth: '800px', margin: '0 auto' }}>
                Traditional audits check compliance. CafetriX Labs decodes your entire cafeteria ecosystem.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '40px'
            }} className="comparison-grid">
              {/* Traditional Audits */}
              <div style={{
                padding: '50px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 107, 107, 0.3)'
              }}>
                <h3 style={{ fontSize: '24px', fontWeight: '600', color: '#FF6B6B', marginBottom: '30px' }}>
                  Traditional Audits
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {traditionalVsCafetrix.traditional.map((item, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px',
                      fontSize: '16px',
                      color: 'rgba(255,255,255,0.7)'
                    }}>
                      <span style={{ color: '#FF6B6B', fontSize: '20px' }}>✕</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CafetriX Labs */}
              <div style={{
                padding: '50px',
                background: 'rgba(0, 212, 255, 0.1)',
                border: '2px solid #00D4FF'
              }}>
                <h3 style={{ fontSize: '24px', fontWeight: '600', color: '#00D4FF', marginBottom: '30px' }}>
                  CafetriX Labs 360°
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {traditionalVsCafetrix.cafetrix.map((item, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px',
                      fontSize: '16px',
                      color: '#ffffff'
                    }}>
                      <CheckCircle2 size={20} color="#00D4FF" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Five Lenses Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid #00D4FF',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#00D4FF',
                fontWeight: '500'
              }}>
                OUR FRAMEWORK
              </div>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Our Five <span style={{ color: '#00D4FF' }}>Powerful Lenses</span>
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)', maxWidth: '700px', margin: '0 auto' }}>
                A comprehensive framework that examines every aspect of your cafeteria operations
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '20px'
            }} className="lenses-grid">
              {fiveLenses.map((lens, index) => (
                <div key={index} style={{
                  padding: '40px 25px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: `2px solid ${lens.color}30`,
                  textAlign: 'center',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = lens.color;
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.background = `${lens.color}10`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${lens.color}30`;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                }}
                >
                  <div style={{ color: lens.color, marginBottom: '20px' }}>
                    {lens.icon}
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: lens.color }}>
                    {lens.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
                    {lens.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Independent Brain Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              padding: '80px',
              background: 'rgba(0, 212, 255, 0.05)',
              border: '2px solid #00D4FF',
              textAlign: 'center'
            }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Your <span style={{ color: '#00D4FF' }}>Independent Brain</span> Between Client & Caterer
              </h2>
              <p className="body-large" style={{ marginBottom: '50px', maxWidth: '800px', margin: '0 auto 50px', color: 'var(--text-muted)' }}>
                CafetriX Labs acts as the neutral expert aligning expectations, numbers, and experience. Every project ends with a clear <strong style={{ color: '#00D4FF' }}>90–180 day action blueprint</strong>.
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '30px',
                textAlign: 'left'
              }} className="blueprint-grid">
                <div>
                  <CheckCircle2 size={24} color="#00D4FF" style={{ marginBottom: '12px' }} />
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Provider Selection</h4>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>Based on operational requirements and right commercial model</p>
                </div>
                <div>
                  <CheckCircle2 size={24} color="#00D4FF" style={{ marginBottom: '12px' }} />
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Pricing Validation</h4>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>Across food costs, labor, overheads, and market benchmarks</p>
                </div>
                <div>
                  <CheckCircle2 size={24} color="#00D4FF" style={{ marginBottom: '12px' }} />
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Layout & Process Changes</h4>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>Menu, staffing, and tech recommendations</p>
                </div>
                <div>
                  <CheckCircle2 size={24} color="#00D4FF" style={{ marginBottom: '12px' }} />
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Projected Savings</h4>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>Quantified financial impact and ROI projections</p>
                </div>
                <div>
                  <CheckCircle2 size={24} color="#00D4FF" style={{ marginBottom: '12px' }} />
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Satisfaction Uplift</h4>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>Employee experience and engagement improvements</p>
                </div>
                <div>
                  <CheckCircle2 size={24} color="#00D4FF" style={{ marginBottom: '12px' }} />
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>Waste Management</h4>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>Food and water waste reduction solutions</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Sandeep Section */}
        <section style={{ 
          background: 'linear-gradient(135deg, #1a1f71 0%, #2d3494 100%)', 
          padding: '100px 7.6923%' 
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '350px 1fr',
              gap: '60px',
              alignItems: 'start'
            }} className="founder-grid">
              <div style={{
                border: '2px solid #00D4FF',
                overflow: 'hidden'
              }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/ell0z8bd_image.png" 
                  alt="Sandeep Devadiga" 
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <div>
                <h2 style={{ 
                  fontSize: '32px', 
                  fontWeight: '700', 
                  color: '#ffffff',
                  marginBottom: '16px'
                }}>
                  Why Sandeep Devadiga's <span style={{ color: '#00D4FF' }}>28+ Years</span> Make CafetriX Labs Unmatched
                </h2>
                <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.9)', marginBottom: '24px', lineHeight: '1.8' }}>
                  With experience managing cafeterias at <strong style={{ color: '#00D4FF' }}>Cisco, Oracle, Google, and Boeing</strong>, Sandeep has been on both sides—as a corporate professional and as a solution creator.
                </p>
                <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.7)', marginBottom: '24px', lineHeight: '1.8' }}>
                  He knows the pressure of serving <strong>5,000 meals daily</strong> while balancing cost, quality, compliance, and fair commercial terms for all parties.
                </p>
                <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.7)', marginBottom: '30px', lineHeight: '1.8' }}>
                  CafetriX Labs combines real-world operational wisdom with consulting discipline to create cafeterias that run smoother, waste less, serve better, and contract smarter.
                </p>
                <a 
                  href="https://www.linkedin.com/in/sandeep31051976" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    background: 'rgba(0, 119, 181, 0.2)',
                    border: '1px solid #0077b5',
                    color: '#0077b5',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#0077b5';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 119, 181, 0.2)';
                    e.currentTarget.style.color = '#0077b5';
                  }}
                >
                  <Linkedin size={18} />
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Who Needs CafetriX Labs */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Who Needs <span style={{ color: '#00D4FF' }}>CafetriX Labs</span> Right Now?
              </h2>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '40px'
            }} className="audience-grid">
              {/* Corporate Facilities Teams */}
              <div style={{
                padding: '50px',
                background: 'rgba(0, 212, 255, 0.05)',
                border: '2px solid #00D4FF'
              }}>
                <Building2 size={48} color="#00D4FF" style={{ marginBottom: '20px' }} />
                <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '24px', color: '#00D4FF' }}>
                  Corporate Facilities Teams
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {[
                    'Provider selection and RFP overload',
                    'Commercial model confusion (fixed vs. variable)',
                    'Rising costs without ROI visibility',
                    'ESG reporting on waste'
                  ].map((item, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <CheckCircle2 size={20} color="#00D4FF" style={{ marginTop: '2px', flexShrink: 0 }} />
                      <span className="body-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Catering Companies */}
              <div style={{
                padding: '50px',
                background: 'rgba(255, 107, 107, 0.05)',
                border: '2px solid #FF6B6B'
              }}>
                <ChefHat size={48} color="#FF6B6B" style={{ marginBottom: '20px' }} />
                <h3 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '24px', color: '#FF6B6B' }}>
                  Catering Companies
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {[
                    'Menu/operations validation',
                    'Kitchen redesign for efficiency',
                    'Win-win commercial support',
                    'Operational excellence benchmarking'
                  ].map((item, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <CheckCircle2 size={20} color="#FF6B6B" style={{ marginTop: '2px', flexShrink: 0 }} />
                      <span className="body-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(0, 212, 255, 0.1)',
                border: '1px solid #00D4FF',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#00D4FF',
                fontWeight: '500'
              }}>
                OUR SERVICES
              </div>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Comprehensive <span style={{ color: '#00D4FF' }}>Cafeteria Solutions</span>
              </h2>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '30px'
            }} className="services-grid">
              {services.map((service, index) => (
                <div key={index} style={{
                  padding: '40px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#00D4FF';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ color: '#00D4FF', marginBottom: '20px' }}>
                    {service.icon}
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '12px' }}>{service.title}</h3>
                  <p className="body-small" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                How We <span style={{ color: '#00D4FF' }}>Work</span>
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
                A systematic approach to transforming your cafeteria operations
              </p>
            </div>

            <div style={{ position: 'relative' }}>
              {/* Timeline Line */}
              <div style={{
                position: 'absolute',
                left: '50%',
                top: 0,
                bottom: 0,
                width: '2px',
                background: 'linear-gradient(to bottom, #00D4FF, #FF6B6B)',
                transform: 'translateX(-50%)'
              }} className="timeline-line" />

              {processSteps.map((step, index) => (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 60px 1fr',
                  gap: '30px',
                  marginBottom: '40px',
                  alignItems: 'center'
                }} className="process-step">
                  {/* Left Content */}
                  <div style={{
                    textAlign: index % 2 === 0 ? 'right' : 'left',
                    order: index % 2 === 0 ? 1 : 3,
                    padding: '30px',
                    background: index % 2 === 0 ? 'rgba(0, 212, 255, 0.05)' : 'transparent',
                    border: index % 2 === 0 ? '1px solid rgba(0, 212, 255, 0.3)' : 'none'
                  }}>
                    {index % 2 === 0 && (
                      <>
                        <span style={{ fontSize: '12px', color: '#00D4FF', fontWeight: '600' }}>{step.phase}</span>
                        <h3 className="heading-2" style={{ marginBottom: '8px' }}>{step.title}</h3>
                        <p className="body-small" style={{ color: 'var(--brand-primary)', marginBottom: '8px' }}>{step.duration}</p>
                        <p className="body-small" style={{ color: 'var(--text-muted)' }}>{step.description}</p>
                      </>
                    )}
                  </div>

                  {/* Center Circle */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: index % 2 === 0 ? '#00D4FF' : '#FF6B6B',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontWeight: '700',
                    fontSize: '20px',
                    order: 2,
                    zIndex: 1
                  }}>
                    {index + 1}
                  </div>

                  {/* Right Content */}
                  <div style={{
                    textAlign: index % 2 === 0 ? 'left' : 'right',
                    order: index % 2 === 0 ? 3 : 1,
                    padding: '30px',
                    background: index % 2 !== 0 ? 'rgba(255, 107, 107, 0.05)' : 'transparent',
                    border: index % 2 !== 0 ? '1px solid rgba(255, 107, 107, 0.3)' : 'none'
                  }}>
                    {index % 2 !== 0 && (
                      <>
                        <span style={{ fontSize: '12px', color: '#FF6B6B', fontWeight: '600' }}>{step.phase}</span>
                        <h3 className="heading-2" style={{ marginBottom: '8px' }}>{step.title}</h3>
                        <p className="body-small" style={{ color: '#FF6B6B', marginBottom: '8px' }}>{step.duration}</p>
                        <p className="body-small" style={{ color: 'var(--text-muted)' }}>{step.description}</p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Frequently Asked <span style={{ color: '#00D4FF' }}>Questions</span>
              </h2>
            </div>

            <div>
              {faqs.map((faq, index) => (
                <div key={index} style={{
                  marginBottom: '16px',
                  border: '1px solid var(--border-subtle)',
                  background: openFaq === index ? 'rgba(0, 212, 255, 0.05)' : 'transparent',
                  transition: 'all 0.3s'
                }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    style={{
                      width: '100%',
                      padding: '24px 30px',
                      background: 'transparent',
                      border: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <span className="heading-3" style={{ color: 'var(--text-primary)' }}>{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp size={24} color="#00D4FF" />
                    ) : (
                      <ChevronDown size={24} color="var(--text-muted)" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div style={{ padding: '0 30px 24px' }}>
                      <p className="body-medium" style={{ color: 'var(--text-muted)', lineHeight: '1.8' }}>
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" style={{ 
          background: 'linear-gradient(135deg, #FF6B6B 0%, #ff5252 100%)', 
          padding: '100px 7.6923%' 
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: '40px', 
              fontWeight: '700', 
              color: '#ffffff',
              marginBottom: '20px'
            }}>
              Ready to Transform Your Cafeteria Ecosystem?
            </h2>
            <p style={{ 
              fontSize: '20px', 
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '40px',
              lineHeight: '1.6'
            }}>
              Book a 30-Minute CafetriX Labs Diagnostic — We'll Review Your Current Setup and Share Three Immediate Wins
            </p>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '40px' }}>
              <Link to="/cafetrix-labs/schedule" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '18px 40px',
                background: '#ffffff',
                color: '#FF6B6B',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '18px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                Schedule a Diagnostic
                <ArrowRight size={20} />
              </Link>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', flexWrap: 'wrap' }}>
              <a href="mailto:sandeep@vdmbi.org" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#ffffff',
                textDecoration: 'none',
                fontSize: '16px'
              }}>
                <Mail size={20} />
                sandeep@vdmbi.org
              </a>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#ffffff',
                fontSize: '16px'
              }}>
                <MapPin size={20} />
                Bengaluru, India
              </span>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 1200px) {
          .lenses-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 1024px) {
          .pain-points-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .services-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .lenses-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .hero-grid, .intro-grid, .comparison-grid, .audience-grid, .founder-grid, .blueprint-grid {
            grid-template-columns: 1fr !important;
          }
          .pain-points-grid, .services-grid, .lenses-grid {
            grid-template-columns: 1fr !important;
          }
          .process-step {
            grid-template-columns: 1fr !important;
          }
          .process-step > div {
            order: 1 !important;
            text-align: left !important;
          }
          .timeline-line {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CafetrixLabsDetail;
